import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import StarRating from '@components/ui/StarRating';
import { useRatings } from '@hooks/useRatings';
import { submitRating } from '@api/ratings';

const RatingModal = ({ isOpen, onClose, activityId, targets }) => {
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const { error } = useRatings(); // simple reuse for error message shape

  useEffect(() => {
    if (!isOpen) {
      setForm({});
      setSubmitting(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleStarChange = (userId, field, value) => {
    setForm((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        [field]: value,
      },
    }));
  };

  const handleCommentChange = (userId, value) => {
    setForm((prev) => ({
      ...prev,
      [userId]: {
        ...(prev[userId] || {}),
        comment: value,
      },
    }));
  };

  const hasAnyRating = Object.values(form).some(
    (v) => v.playerRating || v.hostRating || v.safetyRating
  );

  const handleSubmit = async () => {
    if (!activityId || !targets?.length) return;
    setSubmitting(true);
    try {
      const payloads = targets
        .map((t) => {
          const userId = String(t._id);
          const values = form[userId] || {};
          if (
            values.playerRating == null &&
            values.hostRating == null &&
            values.safetyRating == null
          ) {
            return null;
          }
          const payload = {
            activityId: String(activityId),
            reviewedUserId: userId,
            comment: values.comment || '',
          };
          if (values.playerRating != null) {
            payload.playerRating = values.playerRating;
          }
          if (t.isHost && values.hostRating != null) {
            payload.hostRating = values.hostRating;
          }
          if (values.safetyRating != null) {
            payload.safetyRating = values.safetyRating;
          }
          return payload;
        })
        .filter(Boolean);

      const seenReviewed = new Set();
      const dedupedPayloads = payloads.filter((payload) => {
        const key = String(payload.reviewedUserId);
        if (seenReviewed.has(key)) return false;
        seenReviewed.add(key);
        return true;
      });

      // Submit sequentially to keep backend simple; list is small.
      for (const payload of dedupedPayloads) {
        // eslint-disable-next-line no-await-in-loop
        await submitRating(payload);
      }

      onClose(true);
    } catch (err) {
      setSubmitting(false);
      const data = err.response?.data;
      let msg = data?.message;
      if (Array.isArray(data?.errors) && data.errors.length) {
        const detail = data.errors.map((e) => e.message || e.msg).filter(Boolean).join(' ');
        msg = detail ? `${msg || 'Validation failed'}: ${detail}` : msg;
      }
      toast.error(typeof msg === 'string' && msg.trim() ? msg : 'Could not submit ratings');
    }
  };

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl dark:bg-surface-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-surface-900 dark:text-white">
            Rate participants
          </h2>
          <button
            type="button"
            onClick={() => onClose(false)}
            className="rounded-full p-1 text-surface-400 hover:bg-surface-100 dark:hover:bg-surface-800"
          >
            <X size={16} />
          </button>
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
          {targets.map((t) => {
            const userId = String(t._id);
            const values = form[userId] || {};
            return (
              <div
                key={userId}
                className="rounded-2xl border border-surface-100 bg-surface-50 p-3 dark:border-surface-700 dark:bg-surface-800/50"
              >
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="font-semibold text-surface-900 dark:text-white">
                    {t.name}
                  </div>
                  {t.isHost && (
                    <span className="rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-600 dark:bg-brand-500/10 dark:text-brand-400">
                      Host
                    </span>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-surface-500">
                      Player rating
                    </span>
                    <StarRating
                      rating={values.playerRating || 0}
                      size={16}
                      interactive
                      onChange={(v) =>
                        handleStarChange(userId, 'playerRating', v)
                      }
                    />
                  </div>

                  {t.isHost && (
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-surface-500">
                        Host rating
                      </span>
                      <StarRating
                        rating={values.hostRating || 0}
                        size={16}
                        interactive
                        onChange={(v) =>
                          handleStarChange(userId, 'hostRating', v)
                        }
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-surface-500">
                      Safety rating
                    </span>
                    <StarRating
                      rating={values.safetyRating || 0}
                      size={16}
                      interactive
                      onChange={(v) =>
                        handleStarChange(userId, 'safetyRating', v)
                      }
                    />
                  </div>

                  <textarea
                    rows={2}
                    placeholder="Optional comment"
                    className="mt-2 w-full rounded-2xl border border-surface-200 bg-white px-3 py-2 text-xs text-surface-700 outline-none transition focus:border-brand-300 focus:ring-2 focus:ring-brand-100 dark:border-surface-700 dark:bg-surface-900 dark:text-surface-100"
                    value={values.comment || ''}
                    onChange={(e) =>
                      handleCommentChange(userId, e.target.value)
                    }
                  />
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <p className="mt-2 text-xs text-red-500">
            {error}
          </p>
        )}

        <button
          type="button"
          disabled={!hasAnyRating || submitting}
          onClick={handleSubmit}
          className="mt-4 w-full rounded-2xl bg-brand-500 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Submitting...' : 'Submit ratings'}
        </button>
      </div>
    </div>
  );
};

export default RatingModal;

